/**
 * Client-side checks for the two file inputs in the app.
 *
 * The server is the enforcement point — a check that runs in the page is a
 * courtesy to whoever picked the wrong file, not a control, and anyone willing
 * to open devtools skips it. What it does buy is that the obvious mistakes and
 * the obvious abuse never become a request: a 400MB video, or an `.html` file
 * that would be stored and then served back from a URL on this app's own
 * origin, next to the session cookie.
 *
 * Both lists are deliberately explicit. Widen them when a real workflow needs a
 * format rather than pre-emptively, and keep them in step with whatever the API
 * accepts — a file this passes and the server rejects is a worse experience
 * than one refused at the picker.
 */

const MEGABYTE = 1024 * 1024;

/** Comment attachments: what a reviewer actually attaches to an invoice thread. */
export const MAX_ATTACHMENT_BYTES = 10 * MEGABYTE;

/** A supplier invoice is a PDF, and a large one is already an outlier. */
export const MAX_INVOICE_BYTES = 25 * MEGABYTE;

const ATTACHMENT_EXTENSIONS = new Set([
  "pdf",
  "png", "jpg", "jpeg", "gif", "webp",
  "txt", "csv",
  "xls", "xlsx", "doc", "docx",
]);

const ATTACHMENT_MIME_TYPES = new Set([
  "application/pdf",
  "image/png", "image/jpeg", "image/gif", "image/webp",
  "text/plain", "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);

/** The `accept` attribute for the attachment picker, derived from the same list. */
export const ATTACHMENT_ACCEPT = Array.from(ATTACHMENT_EXTENSIONS)
  .map((extension) => `.${extension}`)
  .join(",");

export const INVOICE_ACCEPT = "application/pdf,.pdf";

function extensionOf(fileName) {
  const parts = String(fileName ?? "").toLowerCase().split(".");
  return parts.length > 1 ? parts.pop() : "";
}

function formatSize(bytes) {
  return `${Math.round(bytes / MEGABYTE)}MB`;
}

/**
 * @typedef {{ ok: true } | { ok: false, reason: string }} FileCheck
 * `reason` is written to be shown to the user as-is.
 */

/**
 * The extension has to be on the list, and a reported MIME type has to agree
 * with it. Browsers leave `type` empty for plenty of legitimate files — an
 * unlabelled `.csv` is common — so an empty type is not by itself a rejection.
 * A renamed `.html`, which is the case that matters, fails on the extension.
 *
 * @param {File} file
 * @returns {FileCheck}
 */
export function validateAttachment(file) {
  if (!file) return { ok: false, reason: "No file selected." };

  if (!ATTACHMENT_EXTENSIONS.has(extensionOf(file.name))) {
    return {
      ok: false,
      reason: "That file type can't be attached. Use a PDF, image, text, spreadsheet or document file.",
    };
  }

  if (file.type && !ATTACHMENT_MIME_TYPES.has(file.type)) {
    return { ok: false, reason: "That file type can't be attached." };
  }

  if (file.size > MAX_ATTACHMENT_BYTES) {
    return {
      ok: false,
      reason: `That file is too large. Attachments are limited to ${formatSize(MAX_ATTACHMENT_BYTES)}.`,
    };
  }

  return { ok: true };
}

/**
 * @param {File} file
 * @returns {FileCheck}
 */
export function validateInvoicePdf(file) {
  if (!file) return { ok: false, reason: "Please choose a PDF file." };

  if (file.type !== "application/pdf" || extensionOf(file.name) !== "pdf") {
    return { ok: false, reason: "Please select a PDF file." };
  }

  if (file.size > MAX_INVOICE_BYTES) {
    return {
      ok: false,
      reason: `That file is too large. Invoices are limited to ${formatSize(MAX_INVOICE_BYTES)}.`,
    };
  }

  return { ok: true };
}
